import React, { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

// Components
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';

// Redux & services
import { KeyValue } from '@/main/models/common';
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from '@/main/services/utils';
import { reportTemplateApi } from '@/main/api';
import { KOGAreaButtons } from '@/main/components/shared/styles';
import { ReportTemplateRequest, ReportTemplateVariableRequest } from '@/main/models/request';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { getOptionsStatus } from '@/main/services/data/data';

// Local components
import SectionFilters from './sectionFilters/sectionFilters';
import SectionGeneralData from './sectionGeneralData/sectionGeneralData';
import SectionHistory from './sectionHistory/sectionHistory';

export interface IFiltersRegisterReportTemplate {
  variable: string;
  type: KeyValue | null;
  guidVariable?: string;
}

const INITIAL_FORM_VALUES = {
  country: null as KeyValue | null,
  companyType: null as KeyValue | null,
  company: null as KeyValue | null,
  reportName: '' as string,
  status: null as KeyValue | null,
  type: null as KeyValue | null,
  filters: [] as IFiltersRegisterReportTemplate[],
}

export type IFormValuesRegisterReportTemplate = typeof INITIAL_FORM_VALUES;

function RegisterReportTemplateScreen() {
  //#region Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, _] = useSearchParams();
  const guidTemplate = searchParams.get('guidTemplate');
  const ID_SCREEN = 'id-registerReportTemplate'
  //#endregion

  //#region States
  const [files, setFiles] = useState<File[]>([]);
  const [optionsVariables, setOptionsVariables] = useState<KeyValue[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [isLoadingDocVariables, setIsLoadingDocVariables] = useState(false);
  const [isReloadHistory, setIsReloadHistory] = useState(false);
  //#endregion

  //#region Form
  const methods = useForm<IFormValuesRegisterReportTemplate>({
    defaultValues: INITIAL_FORM_VALUES,
  });

  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control: methods.control,
    name: 'filters'
  });
  //#endregion

  //#region Async functions
  function onSave(data: IFormValuesRegisterReportTemplate) {
    if (data.filters.length === 0) {
      const messageError = getTranslateByKey('information.uploadDocxTemplateFile', true);
      toast.error(messageError);
      return;
    }

    const variablesList: ReportTemplateVariableRequest[] = data.filters.map(filter => ({
      active: true,
      variable: filter.variable,
      guid: filter.guidVariable || '',
      idReportVariableType: typeof filter.type?.key === 'number'
        ? filter.type.key
        : (filter.type?.key ? Number(filter.type.key) : 0),
    }));

    const request: ReportTemplateRequest = {
      ...(isEdit && { guid: String(guidTemplate) }),
      guidCompany: String(data.company?.key) || '',
      name: data.reportName,
      idReportTemplateType: Number(data.type?.key) || 0,
      idStatusRegister: Number(data.status?.key) || 0,
      variables: variablesList
    };
    setIsLoadingSave(true);
    reportTemplateApi
      .save(removeEmptyValues(request), files[0])
      .then((response) => {

        const messageSuccess = isEdit ? getTranslateByKey('information.editTemplateSuccess') : getTranslateByKey('information.savedSuccess');
        toast.success(messageSuccess);
        navigate(RoutesConstants.registrationSearchReportTemplate)
      })
      .finally(() => {
        setIsLoadingSave(false);
      })
  }

  async function loadOptionTypeVariables() {
    reportTemplateApi
      .getReportVariableTypeDomain()
      .then(response => {
        const orderByValue = response.data.result.sort((a, b) => a.value.localeCompare(b.value));
        setOptionsVariables(orderByValue);
      });
  };

  async function loadEdit() {

    if (isNullUndefined(guidTemplate)) return;
    setIsLoadingEdit(true);
    reportTemplateApi
      .getReportTemplate(guidTemplate)
      .then(response => {
        const { result } = response.data;

        if (isNullUndefined(result)) return;

        const findedStatus = getOptionsStatus().find(status => status.key === result.idStatusRegister);
        methods.setValue('country', { key: result.guidCountry, value: String(result.countryName) });
        methods.setValue('companyType', { key: result.idCompanyType, value: String(result.companyTypeName) });
        methods.setValue('company', { key: result.guidCompany, value: result.companyName });
        methods.setValue('reportName', result.name);
        methods.setValue('status', findedStatus || null);
        methods.setValue('type', { key: result.reportTemplateType, value: result.reportTemplateTypeName });


        remove();
        result.variables.forEach((variable, index) => {
          append({
            guidVariable: variable.guid,
            variable: variable.variable,
            type: { key: variable.idReportVariableType, value: String(variable.reportVariableTypeName) }
          })
        });

      })
      .finally(() => {
        setIsLoadingEdit(false);
      });

  }

  async function onReadFile(files: File[]) {
    if (files.length === 0) return;

    setIsLoadingDocVariables(true);
    reportTemplateApi
      .getDocVariables(files[0])
      .then(response => {        
        const { result } = response.data
        if (!isNullUndefined(result) && result.length > 0) {

          result.forEach(variable => {
            const alreadyExists = fields.some(field => field.variable === variable);

            if (!alreadyExists) {
              append({
                variable,
                type: null
              });
            }
          });

          for (let i = fields.length - 1; i >= 0; i--) {
            const field = fields[i];
            const stillExists = result.includes(field.variable);

            if (!stillExists) {
              remove(i);
            }
          }
        }

      })
      .catch(()=>{
        setFiles([]);
      })
      .finally(() => {
        setIsLoadingDocVariables(false);
      })
  }

  async function onDeleteFile(nameFile: string) {
    remove();
  };
  //#endregion

  //#region Effects
  useEffect(() => {
    loadOptionTypeVariables()
  }, []);

  useEffect(() => {
    if (guidTemplate) {
      setIsEdit(true);
      loadEdit();
    }
  }, [guidTemplate]);
  //#endregion

  const titlePage = `${getTranslateByKey('module.registration')} > ${getTranslateByKey('screen.reportTemplateRegistration')}`;
  const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;

  if (isLoadingEdit) return <LoadingComponent />;

  return (
    <ContainerScreen
      title={titlePage}
      hasBackground
      withSpacing
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSave)} noValidate>
          <SectionGeneralData
            isEdit={isEdit}
            generateIdScreen={generateIdScreen}
          />

          <SectionFilters
            files={files}
            optionsVariables={optionsVariables}
            fields={fields}
            isLoadingDocVariables={isLoadingDocVariables}
            onReadFile={onReadFile}
            onDeleteFile={onDeleteFile}
            setFiles={setFiles}
            generateIdScreen={generateIdScreen}
          />

          {isEdit && (
            <SectionHistory
              guidTemplate={guidTemplate}
              isReloadHistory={isReloadHistory}
            />
          )}

          <KOGAreaButtons $isRegisterScreen>
            <SecondaryButton
              id={generateIdScreen('btn-back')}
              type="button"
              onClick={() => navigate(RoutesConstants.registrationSearchReportTemplate)}
            >
              {getTranslateByKey('information.back')}
            </SecondaryButton>
            <Button
              id={generateIdScreen('btn-save')}
              type='submit'
              isLoading={isLoadingSave}
            >
              {getTranslateByKey('information.save')}
            </Button>
          </KOGAreaButtons>
        </form>
      </FormProvider>
    </ContainerScreen>
  );
}

export default RegisterReportTemplateScreen;